import {persistentStore, flightsStore} from '@/composables/store-accessor'
import {
    B2B_AUTH_EVENT,
    CHANGE_LOCALE_EVENT,
    CHANGE_PRICE_EVENT,
    EventBus,
    RESET as RESET_EVENT,
    RESET_BOOKING_DATA,
    SEARCH_EVENT,
} from '@/utils/event-bus'
import {
    filters as filtersBlank,
    searchRequest as searchRequestBlank,
    searchResponse as searchResponseBlank,
    searchScheduleResponse as searchScheduleResponseBlank,
} from '~src/utils/flights/flights-blank-states.src'
import {appInstance} from '@/utils/app-accessor'
import type {Ref} from 'vue'
import {PRODUCT_NAME} from '@/utils/flights/flights-const'
import {newSearch as newSearchBase, partialSearch} from '@/utils/store-helpers'
import {onmessage} from '@/utils/worker-helpers'
import flightsSort from '@/filters/flightsSort'
import flightsFilter from '~src/filters/flightsFilter.src'
import {useProductRuntimeStore} from '~/composables/product-runtime-store'

export const setupFlightsRuntimeStore = () => {
    const productRuntimeStore = useProductRuntimeStore()
    const {
        searchActiveCount,
        partialSearchTimeoutInstance,
        SET_OFFERS,
        START_SEARCH,
        STOP_SEARCH,
        SET_PARTIAL_SEARCH_RESULTS_CTS,
        SET_PARTIAL_SEARCH_TIMEOUT_INSTANCE,
        STOP_FILTER,
        stopSearch,
        filterActive,
    } = productRuntimeStore

    async function _scheduleSearch(searchRequest: any) {
        const searchResponse = await appInstance.$api.searchFlightSchedule.get(searchRequest)
        if (!searchResponse.routes.length) {
            searchResponse.routes = searchScheduleResponseBlank(searchRequest.routes.length).routes
        }
        flightsStore.SET_SEARCH_SCHEDULE_RESPONSE(searchResponse)
        const routeIndex = 0
        flightsStore.RESET_SCHEDULE_FILTERS(routeIndex)

        //TODO Make by worker
        _filter(searchResponse.routes[routeIndex].offers, {...filtersBlank(), duration: [0, 9000]})
    }

    function _filter(offers: any, filters: any) {
        SET_OFFERS(flightsSort(flightsFilter(offers, filters), flightsStore.sortFnName))
    }

    function _newSearch(searchRequest: any) {
        newSearchBase(stopSearch, SET_OFFERS, searchRequest, searchResponseBlank(), flightsStore)
        SET_FLEXIBLE_DATES({
            startDates: [],
            endDates: [],
        })
        SET_FLEXIBLE_FLIGHTS([])
    }

    function _newScheduleSearch(searchRequest: any) {
        if (scheduleSearchCTS.value) scheduleSearchCTS.value.cancel()
        flightsStore.SET_SEARCH_SCHEDULE_RESPONSE(searchScheduleResponseBlank(searchRequest.routes.length))
        //this.SET_SCHEDULE_OFFERS({offers: []})
        SET_OFFERS([])
        flightsStore.NEW_SCHEDULE_SEARCH(searchRequest)
    }

    const searchType = ref('price')
    const routes: Ref<any[]> = ref([])
    const arrivalLocation: Ref = ref(null)

    const scheduleOffers: Ref<any[]> = ref([])
    const scheduleSearchActiveCount = ref(0)
    const scheduleSearchCTS: Ref = ref(null)
    const scheduleRouteIndex = ref(0)

    const availableOffers: Ref<any[]> = ref([])
    const checkAvailabilityActive = ref(false)

    const flexibleFlights: Ref<any[]> = ref([])
    const flexibleDates = ref({
        startDates: [],
        endDates: [],
    })

    function RESET() {
        flexibleDates.value = {
            startDates: [],
            endDates: [],
        }
        flexibleFlights.value = []
        searchType.value = 'price'
        searchActiveCount.value = 0
    }

    function SET_ROUTES(val: any) {
        routes.value = [...val]
    }

    function SET_ROUTE_DATE({index, date}: any) {
        routes.value[index].date = date
    }

    function SET_FLEXIBLE_DATES(dates: any) {
        flexibleDates.value = dates
    }

    function SET_SCHEDULE_OFFERS({offers: val, route = null}: any) {
        if (route !== null) {
            scheduleOffers.value[route] = val
        } else {
            const index = scheduleOffers.value.length
            for (let i = 0; i < index; i++) {
                scheduleOffers.value[i] = val
            }
        }
    }

    function SET_AVAILABLE_OFFERS(val: any) {
        availableOffers.value = val
    }

    function SET_ACTIVE_ROUTE(val: any) {
        scheduleRouteIndex.value = val
    }

    function START_SCHEDULE_SEARCH() {
        scheduleSearchActiveCount.value++
    }

    function STOP_SCHEDULE_SEARCH() {
        scheduleSearchActiveCount.value--
    }

    function SET_CHECK_AVAILABILITY_ACTIVE(active: any) {
        checkAvailabilityActive.value = active
    }

    function SET_SEARCH_TYPE(type: any) {
        searchType.value = type
    }

    function SET_FLEXIBLE_FLIGHTS(flights: any) {
        flexibleFlights.value = flights
    }

    async function loadRoutes(queryRoutes: any) {
        try {
            const promises: any[] = []
            const routesCodes: any[] = []
            queryRoutes.forEach((route: string) => {
                const codes = route.split(',')[0].split('-')
                routesCodes.push({
                    codes,
                    date: route.split(',')[1],
                })
                codes.forEach(code => {
                    const rq = {
                        pattern: code,
                        limit: 1,
                    }
                    promises.push(appInstance.$api.airports.get(rq))
                })
            })
            const _routes = await Promise.all(promises)
            const maped: any = {}
            _routes.forEach(([route]) => {
                maped[route.iataCode] = Object.assign({}, route, {parentName: route.countryName})
            })
            routes.value = routesCodes.map(routeCodes => {
                return {
                    departure: maped[routeCodes.codes[0]],
                    arrival: maped[routeCodes.codes[1]],
                    date: routeCodes.date,
                }
            })
        } catch (e) {
            routes.value = []
        }
    }

    async function SET_ARRIVAL_LOCATION(id: any) {
        try {
            const {
                cities: [_arrivalLocation],
            } = await appInstance.$api.locations.get({id, limitCities: 1})
            _arrivalLocation.cityName = _arrivalLocation.name
            arrivalLocation.value = _arrivalLocation
        } catch (e) {
            arrivalLocation.value = {}
        }
    }

    function clientInit() {
        EventBus.$on(RESET_EVENT, reset)
        EventBus.$on(RESET_BOOKING_DATA, reset)
        EventBus.$on(B2B_AUTH_EVENT, reset)
        EventBus.$on(CHANGE_PRICE_EVENT, changePrice)
        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
        onmessage(PRODUCT_NAME, STOP_FILTER, filterActive, SET_OFFERS)
    }

    async function changePrice({offerKey, prepareBookResponse}: any) {
        flightsStore.REFRESH_BASKET_PRICE({offerKey, prepareBookResponse})
        persistentStore.REFRESH_CONDITIONS({offerKey, prepareBookResponse})
    }

    function reset() {
        _newSearch(searchRequestBlank())
        _newScheduleSearch(searchRequestBlank())
        RESET()
        flightsStore.RESET()
    }

    function newSearch() {
        _newSearch(searchRequestBlank())
    }

    function newScheduleSearch() {
        _newScheduleSearch(searchRequestBlank())
    }

    async function reload() {
        const promises = []
        promises.push(loadRoutes(flightsStore.searchRequest.routes))

        await Promise.all(promises)
    }

    async function search(rq: any) {
        START_SEARCH()
        _newSearch(rq)
        EventBus.$emit(SEARCH_EVENT)
        try {
            await partialSearch(
                SET_PARTIAL_SEARCH_RESULTS_CTS,
                SET_PARTIAL_SEARCH_TIMEOUT_INSTANCE,
                partialSearchTimeoutInstance,
                rq,
                PRODUCT_NAME,
                flightsStore,
                appInstance.$api.searchFlight,
                'offers',
                ['price', 'duration']
            )
            // eslint-disable-next-line no-empty
        } catch (e) {
        } finally {
            STOP_SEARCH()
        }
    }

    async function scheduleSearch(rq: any) {
        START_SCHEDULE_SEARCH()
        _newScheduleSearch(rq)
        EventBus.$emit(SEARCH_EVENT)
        try {
            await _scheduleSearch(rq)
        } catch (e) {
            console.error(e)
        } finally {
            STOP_SCHEDULE_SEARCH()
        }
    }

    async function checkAvailability(rq: any) {
        SET_CHECK_AVAILABILITY_ACTIVE(true)
        SET_AVAILABLE_OFFERS([])
        flightsStore.SET_AVAILABILITY_RESPONSE(searchResponseBlank())
        flightsStore.SET_AVAILABILITY_REQUEST(rq)
        flightsStore.RESET_AVAILABLE_OFFER_FILTERS()
        EventBus.$emit(SEARCH_EVENT)
        try {
            const rs = await appInstance.$api.checkFlightPricesAndAvailability.post(rq)
            flightsStore.SET_AVAILABLE_OFFER_FILTER({key: 'price', value: [...rs.filters.price]})
            flightsStore.SET_AVAILABLE_OFFER_FILTER({key: 'duration', value: [...rs.filters.duration]})
            flightsStore.SET_AVAILABILITY_RESPONSE(rs)
            // eslint-disable-next-line no-empty
        } catch (e) {
        } finally {
            SET_CHECK_AVAILABILITY_ACTIVE(false)
        }
    }

    async function searchFlexibleFlights(rq: any) {
        SET_FLEXIBLE_FLIGHTS([])
        const {offers} = await appInstance.$api.searchFlight.get(rq)
        const grouped: any = {}
        offers.forEach(({itinerary}: any, index: any) => {
            const {departure} = itinerary[0].segments[0]
            const date = departure.date.split(' ')[0]
            if (!grouped[date]) {
                grouped[date] = []
            }
            grouped[date].push(offers[index])
        })
        const result: any = {}
        Object.entries(grouped).forEach((val: any) => {
            const grouped: any = {}
            val[1].forEach(({itinerary}: any, index: any) => {
                const lastItinerary = itinerary.length - 1
                const lastSegment = itinerary[lastItinerary].segments.length - 1
                const {arrival} = itinerary[lastItinerary].segments[lastSegment]
                const date = arrival.date.split(' ')[0]
                if (!grouped[date]) {
                    grouped[date] = []
                }
                grouped[date].push(val[1][index])
            })
            result[val[0]] = grouped
        })
        const keys = Object.keys(result)
        keys.sort((a, b) => {
            return appInstance.$dateFns.compareAsc(appInstance.$dateFns.parseISO(a), appInstance.$dateFns.parseISO(b))
        })
        const startDates = keys
        const endDates = new Set()
        Object.values(result).forEach((part: any) => {
            Object.keys(part).forEach(date => {
                endDates.add(date)
            })
        })
        const end = Array.from(endDates).sort((a, b) => {
            return appInstance.$dateFns.compareAsc(appInstance.$dateFns.parseISO(a), appInstance.$dateFns.parseISO(b))
        })
        SET_FLEXIBLE_FLIGHTS(result)
        SET_FLEXIBLE_DATES({
            startDates,
            endDates: end,
        })
    }

    function filter({offers, filters}: any) {
        _filter(offers, filters)
    }

    const scheduleSearchActive = computed(() => {
        return scheduleSearchActiveCount.value > 0
    })

    const isPriceMode = computed(() => {
        return searchType.value === 'price'
    })

    const flightsPageLink = computed(() => {
        return (searchRequest: any) => {
            const {partialResponse, convertToCurrency, ...query} = searchRequest
            return {name: 'flights', query}
        }
    })

    const flightsSchedulePageLink = computed(() => {
        return (searchRequest: any) => {
            const {partialResponse, convertToCurrency, ...query} = searchRequest
            return {name: 'flightsSchedule', query}
        }
    })

    const searchRequestFromQuery = computed(() => {
        return (query: any) => {
            //TODO Need filter $route.query params
            const rq = Object.assign(searchRequestBlank(), query)
            if (typeof rq.routes === 'string') {
                rq.routes = [rq.routes]
            }
            if (typeof rq.childrenAges === 'string') {
                rq.childrenAges = [parseInt(rq.childrenAges, 10)]
            }
            rq.convertToCurrency = persistentStore.currency
            rq.adults = parseInt(rq.adults, 10)
            rq.directFlight = rq.directFlight === 'true' || rq.directFlight === true
            rq.baggageOnly = rq.baggageOnly === 'true' || rq.baggageOnly === true
            rq.refundableOnly = rq.refundableOnly === 'true' || rq.refundableOnly === true
            if (rq.citizenshipId) {
                rq.citizenshipId = parseInt(rq.citizenshipId, 10)
            }
            return rq
        }
    })

    return {
        ...productRuntimeStore,
        _newSearch,
        searchType,
        routes,
        arrivalLocation,
        scheduleOffers,
        scheduleSearchActiveCount,
        scheduleSearchCTS,
        scheduleRouteIndex,
        availableOffers,
        checkAvailabilityActive,
        flexibleFlights,
        flexibleDates,
        RESET,
        SET_ROUTES,
        SET_ROUTE_DATE,
        SET_FLEXIBLE_DATES,
        SET_SCHEDULE_OFFERS,
        SET_AVAILABLE_OFFERS,
        SET_ACTIVE_ROUTE,
        START_SCHEDULE_SEARCH,
        STOP_SCHEDULE_SEARCH,
        SET_CHECK_AVAILABILITY_ACTIVE,
        SET_SEARCH_TYPE,
        SET_FLEXIBLE_FLIGHTS,
        loadRoutes,
        SET_ARRIVAL_LOCATION,
        clientInit,
        changePrice,
        reset,
        newSearch,
        newScheduleSearch,
        reload,
        search,
        scheduleSearch,
        checkAvailability,
        searchFlexibleFlights,
        filter,
        scheduleSearchActive,
        isPriceMode,
        flightsPageLink,
        flightsSchedulePageLink,
        searchRequestFromQuery,
    }
}

export const useFlightsRuntimeStore = defineStore('flightsRuntime', setupFlightsRuntimeStore)

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useFlightsRuntimeStore, import.meta.hot))
